/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { EmailRegex, PasswordRegex } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, INodeProps, Modal, ModalBody } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import SignUpButtonComponent from './components/cz-sign-up-button';
import SignUpErrorComponent from './components/cz-sign-up-error';
import SignUpInputComponent, { SignUpMobileNumberInput } from './components/cz-sign-up-input';
import { SignUpDayDropDown, SignUpMonthDropDown, SignUpStateDropDown } from './components/cz-sign-up-dropdown';
import { SignUpTermsAndConditionsCheckbox, SignUpKeepUpCheckbox } from './components/cz-sign-up-checkbox';
import SignUpLabelComponent from './components/cz-sign-up-label';
import SignUpLoadingIconComponent from './components/cz-sign-up-loading-icon';
import SignUpLoadingMessageComponent from './components/cz-sign-up-loading-message';
import SignUpSuccessComponent from './components/cz-sign-up-success';
import { ICzSignUpConfig, ICzSignUpProps, ICzSignUpResources } from './cz-sign-up.props.autogenerated';

export interface ISignUpViewState {
    isShowLoading: boolean;
}

export interface ISignUpItem {
    wrapper: INodeProps;
    key: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input: React.ReactNode;
}
export interface ISignUpDropDown {
    wrapper: INodeProps;
    key: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input: React.ReactNode;
}
export interface ISignUpCheckbox {
    wrapper: INodeProps;
    key: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input: React.ReactNode;
}

export interface ISignUpEmailVerification {
    isRequired: boolean;
    email: ISignUpItem;
    buttonWrapper: INodeProps;
    buttons: React.ReactNode[];
    successMessage: React.ReactNode[];
    errorMessage: React.ReactNode[];
    verificationControlWrapper: INodeProps;
}

/**
 * SignUp Given Name Verification Interface.
 */
export interface ISignUpGivenNameVerification {
    isRequired: boolean;
    givenName: ISignUpItem;
    errorMessage: React.ReactNode[];
    verificationControlWrapper: INodeProps;
}
export interface ISignUpMobileNumberVerification {
    isRequired: boolean;
    extension_MobileNumber: ISignUpItem;
    errorMessage: React.ReactNode[];
    verificationControlWrapper: INodeProps;
}

/**
 * SignUp Surname Verification Interface.
 */
export interface ISignUpSurnameVerification {
    isRequired: boolean;
    surname: ISignUpItem;
    errorMessage: React.ReactNode[];
    verificationControlWrapper: INodeProps;
}

export interface ISignUpLocalAccount {
    localAccount: INodeProps;
    items: ISignUpItem[];
    emailVerification: ISignUpEmailVerification;
    givenNameVerification: ISignUpGivenNameVerification;
    mobileNumberVerification: ISignUpMobileNumberVerification;
    surnameVerification: ISignUpSurnameVerification;
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface ISignUpLoading {
    modal: INodeProps;
    modalBody: INodeProps;
    icon: React.ReactNode;
    message: React.ReactNode;
}

export interface ISignUpViewProps {
    className: string;
    viewState: ISignUpViewState;
    loading: ISignUpLoading;
    signUp: IModuleProps;
    defaultAADConainer: INodeProps;
    aadConainer: INodeProps;
    heading: React.ReactNode;
    signUpLocalAccount: ISignUpLocalAccount;
    resources: any;
    config: any;
}

/**
 *
 * SignUp component
 * All AAD related module is rendered on AAD page and we need to respect HTML contract provide by AAD.
 * Please ensure any change in module don't break contract with AAD.
 * @extends {React.Component<ICzSignUpProps<ICzSignUpConfig>>}
 */
@observer
class SignUp extends React.Component<ICzSignUpProps<ICzSignUpConfig>, any> {
    @observable private isGivenNameVerificationRequried: boolean;

    @observable private isSurnameVerificationRequried: boolean;

    private readonly moduleClassName: string = 'ms-sign-up';

    @observable private emailRegex: string;

    @observable private newPasswordRegex: string;

    @observable private reenterPasswordRegex: string;

    @observable private isEmailVerificationRequried: boolean;

    @observable private isMobileNumberVerificationRequried: boolean;

    @observable private isInitialized: boolean;

    @observable private templateVersion: string;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private initializationTimer: any;

    constructor(props: ICzSignUpProps<ICzSignUpConfig>) {
        super(props);

        this.emailRegex = EmailRegex.defaultRegex.source;
        this.newPasswordRegex = PasswordRegex.defaultRegex.source;
        this.reenterPasswordRegex = PasswordRegex.defaultRegex.source;
        this.isEmailVerificationRequried = false;
        this.isMobileNumberVerificationRequried = false;
        this.isGivenNameVerificationRequried = false;
        this.isSurnameVerificationRequried = false;
        this.isInitialized = false;
        this.templateVersion = '';
    }

    public componentDidMount(): void {
        this._onInit();
    }

    // After successful AAD initialization, call initialize method provided by AAD to attach events.
    public componentDidUpdate(): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (this.isInitialized && window && window['$element'] && window['$element']['initialize']) {
            // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
            window['$element']['initialize']();
        }
    }

    public shouldComponentUpdate(nextProps: ICzSignUpProps<ICzSignUpConfig>, nextState: ISignUpViewState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config, resources } = this.props;

        const viewProps = {
            ...this.props,
            viewState: {
                isShowLoading: !this.isInitialized
            },
            signUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, config.className)
            },
            loading: {
                modal: {
                    tag: Modal,
                    isOpen: true
                },
                modalBody: {
                    tag: ModalBody
                },
                icon: <SignUpLoadingIconComponent className={this.moduleClassName} />,
                message: <SignUpLoadingMessageComponent className={this.moduleClassName} message={resources.loadingMessage} />
            },
            defaultAADConainer: {
                id: 'api',
                style: { display: 'none' }
            },
            aadConainer: {
                id: this.isInitialized ? 'api' : null,
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Msdyn365.Text
                    className={`${this.moduleClassName}__heading`}
                    {...config.heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            signUpLocalAccount: this._renderLocalAccount()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles heading content change event.
     * @param event - Content editable event.
     * @returns - Heading Text.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    /**
     * Handles paragraph content change event.
     * @param event - Content editable event.
     * @returns - Paragraph Text.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.disclaimer = event.target.value);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public _onInit = () => {
        this._prePopulateData();
        this._updateErrorMessage();

        // Check if AAD initialization is complete. AAD do not provide any event to subscribe so we need to check variable set by AAD to check initialization status.
        this.initializationTimer = setInterval(() => {
            this._isInitializationSuccessful();
        }, 100);
        setTimeout(() => {
            clearInterval(this.initializationTimer);
        }, 10_000);
    };

    // After successful AAD initialization, remove waiting and preload any data, if needed.
    private readonly _isInitializationSuccessful = () => {
        window['SA_FIELDS']['AttributeFields'] = window['SA_FIELDS']['AttributeFields'].map((obj: any) => {
            if (obj.ID && obj.ID === 'extension_MobileNumber') {
                obj.IS_REQ = true;
                obj.PAT = this.props.config.mobileNumberPattern ? this.props.config.mobileNumberPattern : '';
                obj.PAT_DESC =
                    this.props.config.mobileNumberPattern && this.props.config.mobileNumberPatternDescription
                        ? this.props.config.mobileNumberPatternDescription
                        : '';
            }
            if (this.props.config.isBirthDateRequired && obj.ID && obj.ID === 'extension_BirthDate') {
                obj.IS_REQ = true;
            }
            if (this.props.config.isBirthDateRequired && obj.ID && obj.ID === 'extension_BirthMonth') {
                obj.IS_REQ = true;
            }
            if (obj.ID && obj.ID === 'extension_AcceptTermsAndConditions') {
                obj.IS_REQ = true;
            }
            if (obj.ID && obj.ID === 'emailVerificationControl' && obj.DISPLAY_CONTROL_CONTENT) {
                obj.DISPLAY_CONTROL_CONTENT.success_send_code_msg = this.props.config.emailVerificationSuccessMessage;
                obj.DISPLAY_CONTROL_CONTENT.success_verify_code_msg = this.props.config.emailSuccessVerifyCodeMessage;
            }
            return obj;
        });
        window['CONTENT']['ViralErrorUserCreationConflict'] = 'A user with the specified ID already exists. Please choose a different one.';
        window['CONTENT']['AADB2C'] = 'A user with the specified mobile number already exists. Please choose a different one.';
        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (window && window['$diags'] && window['$diags']['initializationSuccessful']) {
            clearInterval(this.initializationTimer);
            this.isInitialized = true;
        }
    };

    private readonly _prePopulateData = () => {
        const resources: ICzSignUpResources = this.props.resources || {};

        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (window['SA_FIELDS'] && window['SA_FIELDS']['AttributeFields']) {
            // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
            window['SA_FIELDS']['AttributeFields'].map(
                (obj: { ID: string; PAT: string; UX_INPUT_TYPE: string; PAT_DESC: string; VERIFY: boolean }) => {
                    switch (obj.ID.toLowerCase()) {
                        case 'emailverificationcontrol': {
                            this.isEmailVerificationRequried = true;
                            this.templateVersion = 'v2';
                            break;
                        }
                        case 'mobileverificationcontrol': {
                            this.isMobileNumberVerificationRequried = true;
                            break;
                        }
                        case 'email': {
                            this.emailRegex = obj.PAT;
                            this.isEmailVerificationRequried = obj.VERIFY;
                            this.templateVersion = 'v1';
                            break;
                        }
                        case 'givennameverificationcontrol': {
                            this.isGivenNameVerificationRequried = true;
                            break;
                        }
                        case 'givenname': {
                            this.isGivenNameVerificationRequried = obj.VERIFY;
                            break;
                        }
                        case 'surnameverificationcontrol': {
                            this.isEmailVerificationRequried = true;
                            break;
                        }
                        case 'surname': {
                            this.isSurnameVerificationRequried = obj.VERIFY;
                            break;
                        }
                        case 'newpassword': {
                            this.newPasswordRegex = obj.PAT;
                            break;
                        }
                        case 'reenterpassword': {
                            this.reenterPasswordRegex = obj.PAT;
                            break;
                        }
                        default:
                    }

                    switch (obj.UX_INPUT_TYPE.toLowerCase()) {
                        case 'password': {
                            obj.PAT_DESC = resources.invalidPasswordError;
                            break;
                        }
                        case 'emailbox': {
                            obj.PAT_DESC = resources.invalidEmailAddressError;
                            break;
                        }
                        default:
                    }
                }
            );
        }
    };

    private readonly _updateErrorMessage = () => {
        const resources: ICzSignUpResources = this.props.resources || {};

        // eslint-disable-next-line @typescript-eslint/dot-notation -- Auto-suppressed.
        if (window && window['CONTENT']) {
            const errorMessages = {
                required_field: resources.requiredFieldMissingError
            };

            // eslint-disable-next-line @typescript-eslint/dot-notation -- Auto-suppressed.
            Object.assign(window['CONTENT'], errorMessages);
        }
    };

    private readonly getValue = (v1Value: string, v2Value: string): string => {
        if (this.templateVersion) {
            return this.templateVersion === 'v1' ? v1Value : v2Value;
        }
        return '';
    };
    private _renderLocalAccount(): ISignUpLocalAccount {
        const { config, resources } = this.props;
        const stateOptions = config.states ?? [];
        let defaultValue: any = { label: config.defaultStateValue ?? '', value: config.defaultStateValue ?? '' };
        if (config.showStateField) {
            if (config.states && config.states.length > 0) {
                const isDefaultValue = config.states.find(state => state.value === defaultValue.value);
                if (isDefaultValue) {
                    defaultValue = isDefaultValue;
                } else {
                    defaultValue = config.states[0];
                }
            } else {
                defaultValue = { label: '', value: '' };
            }
        }

        // if (!defaultValue.value && config.states?.length) {
        //     defaultValue = config.states[0];
        // }
        const birthdayLabelText = resources.birthdayLabelText + (config.isBirthDateRequired ? ' *' : '');
        const items = [
            this._renderInput(
                'givenName',
                'text',
                resources.firstNameLabelText,
                resources.firstNameMaxLength,
                undefined,
                resources.firstNameAriaLabel
            ),
            this._renderInput(
                'surname',
                'text',
                resources.lastNameLabelText,
                resources.lastNameMaxLength,
                undefined,
                resources.lastNameAriaLabel
            ),
            this._renderInput(
                'extension_MobileNumber',
                'text',
                resources.mobileNumberLabelText,
                undefined,
                config.mobileNumberPattern,
                resources.mobileNumberAriaLabel,
                '',
                undefined,
                config.mobileNumberPatternDescription,
                config.mobileNumberPlaceHolder
            ),
            this._renderDropDown(
                'extension_BirthMonth',
                birthdayLabelText,
                [
                    { label: 'January', value: 1 },
                    { label: 'February', value: 2 },
                    { label: 'March', value: 3 },
                    { label: 'April', value: 4 },
                    { label: 'May', value: 5 },
                    { label: 'June', value: 6 },
                    { label: 'July', value: 7 },
                    { label: 'August', value: 8 },
                    { label: 'September', value: 9 },
                    { label: 'October', value: 10 },
                    { label: 'November', value: 11 },
                    { label: 'December', value: 12 }
                ],
                resources.monthPlaceHolderText,
                resources.monthAriaLabel
            ),
            this._renderDropDown(
                'extension_BirthDate',
                '',
                [
                    { label: 1, value: 1 },
                    { label: 2, value: 2 },
                    { label: 3, value: 3 },
                    { label: 4, value: 4 },
                    { label: 5, value: 5 },
                    { label: 6, value: 6 },
                    { label: 7, value: 7 },
                    { label: 8, value: 8 },
                    { label: 9, value: 9 },
                    { label: 10, value: 10 },
                    { label: 11, value: 11 },
                    { label: 12, value: 12 },
                    { label: 13, value: 13 },
                    { label: 14, value: 14 },
                    { label: 15, value: 15 },
                    { label: 16, value: 16 },
                    { label: 17, value: 17 },
                    { label: 18, value: 18 },
                    { label: 19, value: 19 },
                    { label: 20, value: 20 },
                    { label: 21, value: 21 },
                    { label: 22, value: 22 },
                    { label: 23, value: 23 },
                    { label: 24, value: 24 },
                    { label: 25, value: 25 },
                    { label: 26, value: 26 },
                    { label: 27, value: 27 },
                    { label: 28, value: 28 },
                    { label: 29, value: 29 },
                    { label: 30, value: 30 },
                    { label: 31, value: 31 }
                ],
                resources.dayPlaceHolderText,
                resources.birthDateAriaLabel
            ),
            this._renderDropDown(
                'extension_State',
                config.showStateField ? config.stateLabel ?? '' : '',
                stateOptions,
                config.statePlaceHolderText ?? '',
                resources.stateAriaLabel,
                ``,
                undefined,
                defaultValue,
                this.props.config.showStateField
            ),
            this._renderInput(
                'email',
                'email',
                resources.emailAddressLabelText,
                undefined,
                this.emailRegex,
                resources.emailAddressAriaLabel
            ),
            this._renderInput(
                'newPassword',
                'password',
                resources.passwordLabelText,
                undefined,
                this.newPasswordRegex,
                resources.passwordAriaLabel
            ),
            this._renderInput(
                'reenterPassword',
                'password',
                resources.confirmPasswordLabelText,
                undefined,
                this.reenterPasswordRegex,
                resources.confirmPasswordAriaLabel
            )
        ];
        if (this.props.config.isUsedByBakery) {
            items.push(
                this._renderCheckbox(
                    'extension_KeepMeUpdated',
                    'extension_KeepMeUpdated',
                    '',
                    config.keepMeUpdatedText,
                    undefined,
                    '',
                    '',
                    this.props.config.isKeepMeUpdatedText ?? false
                )
            );
            items.push(
                this._renderCheckbox(
                    'extension_AcceptTermsAndConditions',
                    'extension_AcceptTermsAndConditions',
                    '',
                    config.acceptTermsAndConditionsText,
                    undefined,
                    resources.privacyPolicyAriaLabel,
                    '',
                    undefined
                )
            );
        }

        return {
            localAccount: {
                id: 'attributeList',
                className: `${this.moduleClassName}__account-items attr`
            },
            items: items,
            emailVerification: {
                isRequired: this.isEmailVerificationRequried,
                email: this._renderInput(
                    this.getValue('email_ver_input', 'emailVerificationCode'),
                    'text',
                    resources.verificationCodeLabelText,
                    undefined,
                    undefined,
                    'Verification Code',
                    'VerificationCode'
                ),
                verificationControlWrapper: {
                    id: 'emailVerificationControl',
                    className: `${this.moduleClassName}__verification-control-content verificationControlContent`
                },
                buttonWrapper: {
                    className: `${this.moduleClassName}__email-verification-buttons ${this.getValue('verify', '')}`,
                    'data-claim_id': 'email'
                },
                buttons: [
                    <SignUpButtonComponent
                        key='email_ver_but_send'
                        id={this.getValue('email_ver_but_send', 'emailVerificationControl_but_send_code')}
                        className={`${this.moduleClassName}__email-verification-button ${
                            this.moduleClassName
                        }__verify-email-send-button ${this.getValue('sendButton', 'sendNewCode')}`}
                        ariaLabel={resources.sendCodeButtonAriaLabel}
                        text={resources.sendCodeButtonText}
                    />,
                    <SignUpButtonComponent
                        key='email_ver_but_verify'
                        id={this.getValue('email_ver_but_verify', 'emailVerificationControl_but_verify_code')}
                        className={`${this.moduleClassName}__email-verification-button ${
                            this.moduleClassName
                        }__verify-email-verify-button ${this.getValue('verifyButton', 'verifyCode')}`}
                        ariaLabel={resources.verifyCodeButtonAriaLabel}
                        text={resources.verifyCodeButtonText}
                    />,
                    <SignUpButtonComponent
                        key='email_ver_but_resend'
                        id={this.getValue('email_ver_but_resend', 'emailVerificationControl_but_send_new_code')}
                        className={`${this.moduleClassName}__email-verification-button ${
                            this.moduleClassName
                        }__verify-email-resend-button ${this.getValue('sendButton', 'sendNewCode')}`}
                        ariaLabel={resources.resendCodeButtonAriaLabel}
                        text={resources.resendCodeButtonText}
                    />,
                    <SignUpButtonComponent
                        key='email_ver_but_edit'
                        id={this.getValue('email_ver_but_edit', 'emailVerificationControl_but_change_claims')}
                        className={`${this.moduleClassName}__email-verification-button ${
                            this.moduleClassName
                        }__verify-email-edit-button ${this.getValue('editButton', 'changeClaims')}`}
                        ariaLabel={resources.changeEmailButtonAriaLabel}
                        text={resources.changeEmailButtonText}
                    />
                ],
                successMessage: [
                    <SignUpSuccessComponent
                        key='email_info'
                        id={this.getValue('email_info', 'emailVerificationControl_info_message')}
                        className={this.moduleClassName}
                        message={resources.verificationCodeSendSuccess}
                    />,
                    <SignUpSuccessComponent
                        key='email_success'
                        id={this.getValue('email_success', 'emailVerificationControl_success_message')}
                        className={this.moduleClassName}
                        message={resources.emailAddressVerifiedSuccess}
                    />
                ],
                errorMessage: [
                    <SignUpErrorComponent
                        key='emailVerificationControl_error_message'
                        id='emailVerificationControl_error_message'
                        className={this.moduleClassName}
                    />,
                    <SignUpErrorComponent
                        key='email_fail_retry'
                        id='email_fail_retry'
                        className={this.moduleClassName}
                        message={resources.retryError}
                    />,
                    <SignUpErrorComponent
                        key='email_fail_no_retry'
                        id='email_fail_no_retry'
                        className={this.moduleClassName}
                        message={resources.retryNotAllowedError}
                    />,
                    <SignUpErrorComponent
                        key='email_fail_throttled'
                        id='email_fail_throttled'
                        className={this.moduleClassName}
                        message={resources.throttledError}
                    />,
                    <SignUpErrorComponent
                        key='email_fail_code_expired'
                        id='email_fail_code_expired'
                        className={this.moduleClassName}
                        message={resources.codeExpiredError}
                    />,
                    <SignUpErrorComponent
                        key='email_fail_server'
                        id='email_fail_server'
                        className={this.moduleClassName}
                        message={resources.serverError}
                    />,
                    <SignUpErrorComponent
                        key='email_incorrect_format'
                        id='email_incorrect_format'
                        className={this.moduleClassName}
                        message={resources.invalidEmailError}
                    />
                ]
            },
            givenNameVerification: {
                isRequired: this.isGivenNameVerificationRequried,
                givenName: this._renderInput(
                    this.getValue('givenName_ver_input', 'givenNameVerificationCode'),
                    'text',
                    resources.verificationCodeLabelText,
                    undefined,
                    undefined,
                    undefined,
                    'VerificationCode'
                ),
                verificationControlWrapper: {
                    id: 'givenNameVerificationControl',
                    className: `${this.moduleClassName}__verification-control-content verificationControlContent`
                },
                errorMessage: [
                    <SignUpErrorComponent
                        key='requiredFieldMissing'
                        id='requiredFieldMissing'
                        className={this.moduleClassName}
                        message={resources.requiredFieldMissingSummaryError}
                    />
                ]
            },
            mobileNumberVerification: {
                isRequired: this.isMobileNumberVerificationRequried,
                extension_MobileNumber: this._renderInput(
                    this.getValue('mobileNumber_ver_input', 'mobileNumberVerificationCode'),
                    'number',
                    resources.verificationCodeLabelText,
                    undefined,
                    undefined,
                    undefined,
                    'VerificationCode'
                ),
                verificationControlWrapper: {
                    id: 'mobileNumberVerificationControl',
                    className: `${this.moduleClassName}__verification-control-content verificationControlContent`
                },
                errorMessage: [
                    <SignUpErrorComponent
                        key='requiredFieldMissing'
                        id='requiredFieldMissing'
                        className={this.moduleClassName}
                        message={resources.requiredFieldMissingSummaryError}
                    />
                ]
            },
            surnameVerification: {
                isRequired: this.isSurnameVerificationRequried,
                surname: this._renderInput(
                    this.getValue('surname_ver_input', 'surnameVerificationCode'),
                    'text',
                    resources.verificationCodeLabelText,
                    undefined,
                    undefined,
                    undefined,
                    'VerificationCode'
                ),
                verificationControlWrapper: {
                    id: 'surnameVerificationControl',
                    className: `${this.moduleClassName}__verification-control-content verificationControlContent`
                },
                errorMessage: [
                    <SignUpErrorComponent
                        key='requiredFieldMissing'
                        id='requiredFieldMissing'
                        className={this.moduleClassName}
                        message={resources.requiredFieldMissingSummaryError}
                    />
                ]
            },
            buttons: [
                <SignUpButtonComponent
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={resources.signUpButtonArialabel}
                    text={resources.signUpButtonText}
                    disabled={true}
                />,
                <SignUpButtonComponent
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={resources.cancelButtonArialabel}
                    text={resources.cancelButtonText}
                />
            ],
            errorMessage: [
                <SignUpErrorComponent
                    key='passwordEntryMismatch'
                    id='passwordEntryMismatch'
                    className={this.moduleClassName}
                    message={resources.passwordEntryMismatchError}
                />,
                <SignUpErrorComponent
                    key='requiredFieldMissing'
                    id='requiredFieldMissing'
                    className={this.moduleClassName}
                    message={resources.requiredFieldMissingSummaryError}
                />,
                <SignUpErrorComponent
                    key='fieldIncorrect'
                    id='fieldIncorrect'
                    className={this.moduleClassName}
                    message={resources.fieldIncorrectError}
                />,
                <SignUpErrorComponent
                    key='claimVerificationServerError'
                    id='claimVerificationServerError'
                    className={this.moduleClassName}
                    message={resources.claimVerificationServerError}
                />,
                <SignUpErrorComponent
                    key='ViralErrorUserCreationConflict'
                    id='ViralErrorUserCreationConflict'
                    className={this.moduleClassName}
                    message={resources.viralErrorUserCreationConflict}
                />
            ],
            disclaimer: config.disclaimer && (
                <Msdyn365.RichTextComponent
                    className={`${this.moduleClassName}__sign-up-disclaimer`}
                    text={config.disclaimer}
                    editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
                />
            )
        };
    }

    private _renderInput(
        id: string,
        type: string,
        labelText: string,
        maxLength?: string,
        pattern?: string,
        ariaLabel?: string,
        cssClassName?: string,
        value?: string,
        errorMessage?: string,
        placeHolder?: string,
        onChange?: any
    ): ISignUpItem {
        const className = `${this.moduleClassName}__account-item`;
        // let minLength = 10;
        // let ausPatternMisMatch = false;
        // if (this.props.config.country === 'CAN') {
        //     minLength = 12;
        // }
        // if (this.props.config.country === 'AUS' && this.state.mobileNumber !== '') {
        //     const substringToMatched = this.state.mobileNumber.substring(0, 2);
        //     ausPatternMisMatch = substringToMatched !== '04';
        // }
        return {
            wrapper: {
                className: classnames(className, `${className}-${id}`, 'entry-item', 'attrEntry', cssClassName)
            },
            key: id,
            label: (
                <SignUpLabelComponent
                    {...{
                        id,
                        forId: id,
                        className: classnames(className, cssClassName),
                        text: labelText
                    }}
                />
            ),
            errorMessage: (
                <SignUpErrorComponent
                    {...{
                        className: this.moduleClassName,
                        type: 'item'
                    }}
                />
            ),

            // <>
            //     {' '}
            //     {this.state.mobileNumber !== '' && this.props.config.country === 'AUS' && ausPatternMisMatch && (
            //         <span key='mobileNumberPatternError' className='ms-sign-up__item-error' aria-hidden='false'>
            //             {this.props.config.mobileNumberPatternErrorMessage}
            //         </span>
            //     )}
            //     {this.state.mobileNumber !== '' && this.state.mobileNumber.length < minLength && (
            //         <span key='mobileNumberMinLengthError' className='ms-sign-up__item-error' aria-hidden='false'>
            //             {this.props.config.mobileNumberLengthErrorMessage}
            //         </span>
            //     )}
            // </>
            input:
                id === 'extension_MobileNumber' ? (
                    <SignUpMobileNumberInput
                        {...{
                            id,
                            type,
                            value,
                            maxLength,
                            pattern,
                            className,
                            errorMessage,
                            placeHolder,
                            ariaLabel,
                            country: this.props.config.country
                        }}
                    />
                ) : (
                    <SignUpInputComponent
                        {...{
                            id,
                            type,
                            value,
                            maxLength,
                            pattern,
                            className,
                            ariaLabel
                        }}
                    />
                )
        };
    }

    private _renderDropDown(
        id: string,
        labelText: string,
        options: any,
        placeHolderText?: string,
        ariaLabel?: string,
        cssClassName?: string,
        onChange?: any,
        defaultValue?: any,
        showStateField?: any
    ): ISignUpItem {
        const className = `${this.moduleClassName}__account-item`;
        return {
            wrapper: {
                className: classnames(className, `${className}-${id}`, 'entry-item', 'attrEntry', cssClassName)
            },
            key: id,
            label: (
                <SignUpLabelComponent
                    {...{
                        id,
                        forId: id,
                        className,
                        text: labelText
                    }}
                />
            ),
            errorMessage: [
                // <>
                //     {id === 'extension_BirthMonth' && this.state.selectedMonth === '' && this.state.selectedDate !== '' && (
                //         <span key='selectBirthMonthError' className='ms-sign-up__item-error' aria-hidden='false'>
                //             Please select a birthday month
                //         </span>
                //     )}
                // </>,
                // <>
                //     {id === 'extension_BirthDate' && this.state.selectedMonth !== '' && this.state.selectedDate === '' && (
                //         <span key='selectBirthDayError' className='ms-sign-up__item-error' aria-hidden='false'>
                //             Please select a birthday Date
                //         </span>
                //     )}
                // </>,
                <SignUpErrorComponent
                    key='birthDayError'
                    {...{
                        className: this.moduleClassName,
                        type: 'item'
                    }}
                />
            ],
            input:
                id === 'extension_BirthDate' ? (
                    <SignUpDayDropDown
                        {...{
                            id,
                            options,
                            placeHolderText,
                            className,
                            onChange,
                            dependencyBirthReqFlag: this.props.config.isBirthDateRequired,
                            ariaLabel,
                            birthDayDatePatternDescription: this.props.config.birthDayDatePatternDescription
                        }}
                    />
                ) : id === 'extension_BirthMonth' ? (
                    <SignUpMonthDropDown
                        {...{
                            id,
                            options,
                            placeHolderText,
                            className,
                            onChange,
                            dependencyBirthReqFlag: this.props.config.isBirthDateRequired,
                            ariaLabel
                        }}
                    />
                ) : (
                    <SignUpStateDropDown
                        {...{
                            id,
                            options,
                            placeHolderText,
                            className,
                            ariaLabel,
                            defaultValue,
                            showStateField
                        }}
                    />
                )
        };
    }

    private _renderCheckbox(
        id: string,
        name: string,
        labelText: string,
        text?: any,
        onChange?: any,
        ariaLabel?: string,
        cssClassName?: string,
        isCheckedValue?: boolean
    ): ISignUpCheckbox {
        const className = `${this.moduleClassName}__account-item`;
        return {
            wrapper: {
                className: classnames(className, `${className}-${id}`, 'entry-item', 'attrEntry', cssClassName)
            },
            key: id,
            label: (
                <SignUpLabelComponent
                    {...{
                        id,
                        forId: id,
                        className,
                        text: labelText
                    }}
                />
            ),
            errorMessage: (
                <SignUpErrorComponent
                    {...{
                        className: this.moduleClassName,
                        type: 'item'
                    }}
                />
            ),
            input:
                id === 'extension_AcceptTermsAndConditions' ? (
                    <SignUpTermsAndConditionsCheckbox
                        {...{
                            id,
                            name,
                            text,
                            ariaLabel,
                            onChange,
                            className
                        }}
                    />
                ) : (
                    <SignUpKeepUpCheckbox
                        {...{
                            id,
                            name,
                            text,
                            undefined,
                            onChange,
                            className,
                            isCheckedValue: isCheckedValue ?? undefined
                        }}
                    />
                )
        };
    }
}

export default SignUp;
